import { ChevronRight, Upload } from '@mui/icons-material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { Button, IconButton, ListItemText, Menu, MenuItem } from '@mui/material'
import {
  CargoType,
  CarrierType,
  CarrierVisitDirection,
  CarrierVisitStatus,
} from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import { OrderNavigationDirection, useOrderNavigation } from '@planning/hooks/useOrderNavigation'
import { useRailVisitBreadcrumbs } from '@planning/hooks/useRailVisitBreadcrumbs'
import { CreateGeneralCargoOrder } from '@planning/pages/Order/CreateGeneralCargoOrders'
import { IRailVisitItem } from '@planning/rt-stores/railVisit/RailVisitItem'
import { orderListParsingService } from '@planning/services'
import { useTranslate } from '@tolgee/react'
import { Breadcrumbs, Header } from '@tom-ui/ui'
import { downloadFile } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import Papa from 'papaparse'
import { FC, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet-async'

type Props = {
  railVisitItem: IRailVisitItem
  direction: OrderNavigationDirection
  onUploadClick: () => void
  onAssignRailTrack: () => void
  viewMode: 'list' | 'plan'
}

export const RailVisitOrderListHeader: FC<Props> = observer(
  ({ railVisitItem, direction, viewMode, onUploadClick, onAssignRailTrack }) => {
    const { t } = useTranslate()
    const { navigateVisitOrderPage, navigateAddEmptyRailcarPage } = useOrderNavigation()
    const { dialogStore, generalCargoViewStore, railcarTrackPositionItemStore } = usePlanningStore()

    const { breadcrumbs, railVisitLabel } = useRailVisitBreadcrumbs(railVisitItem)

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const [subMenuAnchorEl, setSubMenuAnchorEl] = useState<null | HTMLElement>(null)

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget)
    }

    const handleMenuClose = () => {
      setAnchorEl(null)
      setSubMenuAnchorEl(null)
    }

    const handleSubMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
      setSubMenuAnchorEl(event.currentTarget)
    }

    const directionLabel = useMemo(() => {
      return direction === 'load'
        ? t('loadList', 'Load List')
        : t('dischargeList', 'Discharge List')
    }, [direction, t])

    const title = useMemo(
      () => (railVisitLabel ? `${railVisitLabel} - ${directionLabel}` : directionLabel),
      [railVisitLabel, directionLabel],
    )

    const carrierDirection =
      direction === 'discharge' ? CarrierVisitDirection.Inbound : CarrierVisitDirection.Outbound

    const handleExportOrders = () => {
      const { fileName, header, data } = orderListParsingService.parseOrderListExport(
        CarrierType.Train,
        carrierDirection,
        railVisitItem,
        railcarTrackPositionItemStore.elements,
      )

      const csvData = Papa.unparse({ fields: header, data })
      downloadFile(new Blob([csvData as BlobPart]), fileName)
      handleMenuClose()
    }

    const handleAddOrder = async () => {
      if (!railVisitItem?.id) return

      if (isGeneralCargoVisit) {
        await generalCargoViewStore.getPackages()

        dialogStore.openDialog(
          <CreateGeneralCargoOrder
            carrierVisit={railVisitItem.data}
            carrierVisitType={CarrierType.Train}
            forcedDirection={carrierDirection}
            disableCarrierVisitSelection
          />,
          true,
          {
            open: true,
            maxWidth: 'lg',
          },
        )
      } else {
        navigateVisitOrderPage({
          carrierVisitId: railVisitItem.id,
          carrierVisitType: CarrierType.Train,
          direction,
        })
      }
      handleMenuClose()
    }

    const handleNavigateToAddEmptyRailcar = () => {
      if (!railVisitItem?.id) return
      navigateAddEmptyRailcarPage(railVisitItem.id, direction)
      handleMenuClose()
    }

    const isEditDisabled =
      railVisitItem.data.status === CarrierVisitStatus.Cancelled ||
      railVisitItem.data.status === CarrierVisitStatus.Closed

    const isGeneralCargoVisit = railVisitItem?.data.cargoType === CargoType.GeneralCargo

    return (
      <>
        <Helmet>
          <title>{title}</title>
        </Helmet>

        <Breadcrumbs items={breadcrumbs} />

        <Header
          title={directionLabel}
          position='relative'
          rightRenderOption={
            <>
              <Button
                variant='contained'
                color='primary'
                data-cy='upload-orders-btn'
                onClick={onUploadClick}
                startIcon={<Upload />}
                disabled={isEditDisabled}
              >
                {t('upload', 'Upload')}
              </Button>

              <IconButton onClick={handleMenuOpen} data-cy='visit-orders-options-btn'>
                <MoreVertIcon />
              </IconButton>

              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem
                  onClick={handleSubMenuOpen}
                  disabled={isEditDisabled}
                  sx={{ display: 'flex', justifyContent: 'space-between', minWidth: '180px' }}
                >
                  <ListItemText>{t('add', 'Add')}</ListItemText>
                  <ChevronRight fontSize='small' />
                </MenuItem>

                {!isGeneralCargoVisit && (
                  <MenuItem onClick={handleExportOrders}>
                    {t('exportOrders', 'Export orders')}
                  </MenuItem>
                )}

                {viewMode === 'plan' && (
                  <MenuItem
                    onClick={() => {
                      onAssignRailTrack()
                      handleMenuClose()
                    }}
                    disabled={isEditDisabled}
                  >
                    {t('assignRailTrack', 'Assign rail track')}
                  </MenuItem>
                )}
              </Menu>

              <Menu
                anchorEl={subMenuAnchorEl}
                open={Boolean(subMenuAnchorEl)}
                onClose={() => setSubMenuAnchorEl(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem onClick={handleAddOrder}>{t('order', 'Order')}</MenuItem>

                {viewMode === 'plan' && (
                  <MenuItem onClick={handleNavigateToAddEmptyRailcar}>
                    {t('emptyRailcar', 'Empty railcar')}
                  </MenuItem>
                )}
              </Menu>
            </>
          }
        />
      </>
    )
  },
)
